import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert, Badge, Button, Card, Col, Dropdown, Form, Modal,
  Row, Spinner, Table,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";
import TaskReturnModal from "../components/TaskReturnModal";

// ── constants ──────────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "week",  label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year",  label: "This Year" },
  { key: "all",   label: "All Time" },
];

// Reporting windows for the Services Summary Report (see ServicesReportPage).
const REPORT_PERIOD_OPTIONS = [
  { key: "month",     label: "This Month" },
  { key: "quarter",   label: "This Quarter" },
  { key: "year",      label: "This Year" },
  { key: "last_year", label: "Last Year" },
];

const SERVICE_CARDS = [
  { key: "bus_cards",  label: "Bus Cards",       hasTotal: true },
  { key: "gas",        label: "Gas",             hasTotal: true },
  { key: "propane",    label: "Propane",         hasTotal: true },
  { key: "bill_pay",   label: "Help Pay a Bill", hasTotal: true },
  { key: "rides",      label: "Rides Given" },
  { key: "food_cards", label: "Food Cards",      hasTotal: true },
  { key: "pantry",     label: "Pantry Referrals" },
];

const DETAIL_COLS = {
  bus_cards: [
    { key: "client_name",  label: "Client",     render: (v, row) => <Link to={`/clients/${row.client_id}/view`}>{v}</Link> },
    { key: "service_date", label: "Date" },
    { key: "status",       label: "Status",     render: (v) => <StatusBadge status={v} /> },
    { key: "created_by",   label: "Added By" },
  ],
  gas: [
    { key: "client_name",  label: "Client",     render: (v, row) => <Link to={`/clients/${row.client_id}/view`}>{v}</Link> },
    { key: "service_date", label: "Date" },
    { key: "gas_type",     label: "Type" },
    { key: "gas_amount",   label: "Amount",     render: (v) => v != null ? `$${Number(v).toFixed(2)}` : "—" },
    { key: "status",       label: "Status",     render: (v) => <StatusBadge status={v} /> },
    { key: "created_by",   label: "Added By" },
  ],
  propane: [
    { key: "client_name",          label: "Client",     render: (v, row) => <Link to={`/clients/${row.client_id}/view`}>{v}</Link> },
    { key: "service_date",         label: "Date" },
    { key: "propane_tank_service", label: "Service Type" },
    { key: "propane_type",         label: "Tank Type" },
    { key: "propane_gallons",      label: "Gallons",    render: (v) => v != null ? Number(v).toFixed(2) : "—" },
    { key: "status",               label: "Status",     render: (v) => <StatusBadge status={v} /> },
    { key: "created_by",           label: "Added By" },
  ],
  bill_pay: [
    { key: "client_name",      label: "Client",       render: (v, row) => <Link to={`/clients/${row.client_id}/view`}>{v}</Link> },
    { key: "service_date",     label: "Date" },
    { key: "bill_provider",    label: "Provider" },
    { key: "bill_type",        label: "Bill Type" },
    { key: "bill_amount_due",  label: "Amount Due",   render: (v) => v != null ? `$${Number(v).toFixed(2)}` : "—" },
    { key: "bill_hhh_amount",  label: "HHH Amount",   render: (v) => v != null ? `$${Number(v).toFixed(2)}` : "—" },
    { key: "status",           label: "Status",       render: (v) => <StatusBadge status={v} /> },
    { key: "created_by",       label: "Added By" },
  ],
  rides: [
    { key: "client_name",      label: "Client",       render: (v, row) => <Link to={`/clients/${row.client_id}/view`}>{v}</Link> },
    { key: "service_date",     label: "Date" },
    { key: "pickup_location",  label: "Pickup" },
    { key: "dropoff_location", label: "Dropoff" },
    { key: "drivers",          label: "Driver(s)",    render: (v) => (v && v.length ? v.join(", ") : "—") },
    { key: "status",           label: "Status",       render: (v) => <StatusBadge status={v} /> },
  ],
  food_cards: [
    { key: "client_name",     label: "Client",     render: (v, row) => <Link to={`/clients/${row.client_id}/view`}>{v}</Link> },
    { key: "service_date",    label: "Date" },
    { key: "food_card_value", label: "Value",      render: (v) => v != null ? `$${Number(v).toFixed(2)}` : "—" },
    { key: "food_card_qty",   label: "Qty" },
    { key: "status",          label: "Status",     render: (v) => <StatusBadge status={v} /> },
    { key: "created_by",      label: "Added By" },
  ],
  pantry: [
    { key: "client_name",  label: "Client",     render: (v, row) => <Link to={`/clients/${row.client_id}/view`}>{v}</Link> },
    { key: "service_date", label: "Date" },
    { key: "status",       label: "Status",     render: (v) => <StatusBadge status={v} /> },
    { key: "created_by",   label: "Added By" },
  ],
};

const STATUS_VARIANT = {
  Open: "secondary",
  "Pending Approval": "warning",
  "Pending Verification": "warning",
  Approved: "success",
  Confirmed: "info",
  Assigned: "primary",
  Returned: "danger",
  Denied: "danger",
  COMPLETED: "success",
  Closed: "dark",
  "Request Cancelled": "danger",
};

// ── small helpers ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  if (status === "Complete") {
    return <span className="badge" style={{ backgroundColor: "#6f42c1", color: "#fff" }}>{status}</span>;
  }
  if (status === "Pending") {
    return <span className="badge" style={{ backgroundColor: "#fd7e14", color: "#fff" }}>{status}</span>;
  }
  const bg = STATUS_VARIANT[status] || "secondary";
  return (
    <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>
      {status}
    </Badge>
  );
}

function fmt$(n) {
  return n != null ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "$0.00";
}

// ── main component ─────────────────────────────────────────────────────────────

export default function AdminDashboardPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [period, setPeriod]           = useState("month");
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");

  // service-type detail modal (bus cards, propane, etc.)
  const [detailType, setDetailType]   = useState(null);
  const [detailData, setDetailData]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  // assignee services modal (my assignments + other assignees)
  const [assigneeModal, setAssigneeModal] = useState({
    show: false, name: "", items: [], loading: false, error: "",
  });

  // Task being marked complete / sent back / reassigned from the My Open Tasks card
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [returningTask, setReturningTask] = useState(null);
  const [assigningTaskId, setAssigningTaskId] = useState(null);
  const [assignees, setAssignees] = useState([]);

  // Services report parameter modal
  const [reportModal, setReportModal]   = useState(false);
  const [reportPeriod, setReportPeriod] = useState("month");

  // quiet=true refetches in place, so an inline action (completing a task)
  // doesn't replace the whole dashboard with the full-page spinner.
  const load = useCallback((quiet = false) => {
    if (!quiet) setLoading(true);
    setError("");
    return api
      .get("/dashboard/admin", { params: { period } })
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load admin dashboard."))
      .finally(() => { if (!quiet) setLoading(false); });
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // Who a task can be handed to. Same list the create-task picker uses. Loaded
  // once; a failure just leaves the Reassign menu empty.
  useEffect(() => {
    api.get("/tasks/intake-workers")
      .then((res) => setAssignees(res.data.items || []))
      .catch(() => setAssignees([]));
  }, []);

  const completeTask = async (taskId) => {
    setCompletingTaskId(taskId);
    try {
      await api.put(`/tasks/${taskId}/complete`);
      await load(true);
    } catch {
      setError("Failed to mark the task complete. Please try again.");
    } finally {
      setCompletingTaskId(null);
    }
  };

  // Hand a task back to its creator with a note. Errors propagate so the modal
  // can show them without the user losing what they typed.
  const returnTask = async (note) => {
    await api.put(`/tasks/${returningTask.task_id}/return`, { note });
    setReturningTask(null);
    await load(true);
  };

  const assignTask = async (taskId, userId) => {
    setAssigningTaskId(taskId);
    setError("");
    try {
      await api.put(`/tasks/${taskId}/assign`, { assigned_to: userId });
      await load(true);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to reassign the task.");
    } finally {
      setAssigningTaskId(null);
    }
  };

  const openDetail = async (type) => {
    setDetailType(type);
    setDetailData(null);
    setDetailError("");
    setDetailLoading(true);
    try {
      const res = await api.get("/dashboard/admin/service-details", {
        params: { type, period },
      });
      setDetailData(res.data);
    } catch {
      setDetailError("Failed to load details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailType(null);
    setDetailData(null);
  };

  const openAssigneeModal = async (userId, name) => {
    setAssigneeModal({ show: true, name, items: [], loading: true, error: "" });
    try {
      const res = await api.get("/dashboard/admin/assignee-services", {
        params: { user_id: userId },
      });
      setAssigneeModal((prev) => ({ ...prev, items: res.data.items, loading: false }));
    } catch {
      setAssigneeModal((prev) => ({
        ...prev, loading: false, error: "Failed to load services.",
      }));
    }
  };

  const closeAssigneeModal = () =>
    setAssigneeModal({ show: false, name: "", items: [], loading: false, error: "" });

  const generateReport = () => {
    setReportModal(false);
    navigate(`/reports/services?period=${reportPeriod}`);
  };

  const viewOnClientPage = (clientId, serviceId) => {
    closeAssigneeModal();
    navigate(`/clients/${clientId}/view`, { state: { openServiceId: serviceId } });
  };

  // ── render ────────────────────────────────────────────────────────────────

  const periodLabel = PERIOD_OPTIONS.find((p) => p.key === period)?.label || "";

  return (
    <>
      {/* ── Reports ───────────────────────────────────────────────────────── */}
      <Card className="shadow-sm mb-4 border-primary-subtle">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h6 className="mb-1 text-uppercase text-muted" style={{ letterSpacing: "0.05em" }}>
              <i className="bi bi-file-earmark-bar-graph me-2" />Reports
            </h6>
            <div className="text-muted small">Services Summary — counts by category and month, ready to print.</div>
          </div>
          <Button variant="primary" onClick={() => setReportModal(true)}>
            <i className="bi bi-file-earmark-text me-1" /> Services Summary Report
          </Button>
        </Card.Body>
      </Card>

      {/* Header + period filter */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <h4 className="mb-0">Admin Dashboard</h4>
        <div className="btn-group">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.key}
              className={`btn btn-sm ${period === p.key ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : data && (
        <>
          {/* ── Summary totals ──────────────────────────────────────────── */}
          <Row className="g-3 mb-4">
            <Col xs={12} sm={4}>
              <Card className="text-center shadow-sm h-100">
                <Card.Body>
                  <div style={{ fontSize: "2rem", fontWeight: 700 }}>{data.totals.clients}</div>
                  <div className="text-muted small">Clients Helped ({periodLabel})</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={4}>
              <Card className="text-center shadow-sm h-100">
                <Card.Body>
                  <div style={{ fontSize: "2rem", fontWeight: 700 }}>{data.totals.services}</div>
                  <div className="text-muted small">Services Given ({periodLabel})</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={4}>
              <Card
                className="shadow-sm h-100"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/admin/beds")}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = ""}
              >
                <Card.Body>
                  <div className="text-muted small mb-2 fw-semibold text-uppercase" style={{ letterSpacing: "0.05em" }}>
                    Beds in Stock
                  </div>
                  {["Twin", "Full"].map((size) => {
                    const s = data.beds_summary?.[size] || {};
                    const availMatt = Math.max(0, (s.mattresses ?? 0) - (s.mattresses_pending ?? 0) - (s.mattresses_given ?? 0));
                    const availBox  = Math.max(0, (s.box_springs ?? 0) - (s.box_springs_pending ?? 0) - (s.box_springs_given ?? 0));
                    const hasPending = (s.mattresses_pending ?? 0) > 0 || (s.box_springs_pending ?? 0) > 0;
                    return (
                      <div key={size} className="mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold small">{size}</span>
                          {hasPending && (
                            <Badge bg="warning" text="dark" style={{ fontSize: "0.68rem" }}>pending</Badge>
                          )}
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted" style={{ fontSize: "0.75rem" }}>Matt: <strong>{availMatt}</strong></span>
                          <span className="text-muted" style={{ fontSize: "0.75rem" }}>Box Spr: <strong>{availBox}</strong></span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-top mt-2 pt-1 d-flex justify-content-between align-items-center">
                    <span className="text-muted small fw-semibold">Frames (Universal)</span>
                    <span className="text-muted small d-flex align-items-center gap-1">
                      <strong>{Math.max(0, (data.beds_summary?.Frames?.frames ?? 0) - (data.beds_summary?.Frames?.pending ?? 0) - (data.beds_summary?.Frames?.given_out ?? 0))}</strong>
                      {(data.beds_summary?.Frames?.pending ?? 0) > 0 && (
                        <Badge bg="warning" text="dark" style={{ fontSize: "0.68rem" }}>pending</Badge>
                      )}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Service count cards ─────────────────────────────────────── */}
          <h6 className="text-uppercase text-muted mb-2" style={{ letterSpacing: "0.05em" }}>
            Approved Services — {periodLabel} <span className="text-muted fw-normal">(click for details)</span>
          </h6>
          <Row className="g-2 mb-4">
            {SERVICE_CARDS.map(({ key, label, hasTotal }) => {
              const sc = data.service_counts;
              const entry = sc[key];
              const count = hasTotal ? (entry?.count ?? 0) : (entry ?? 0);
              return (
                <Col key={key} xs={6} sm={4} md={3} xl={2}>
                  <Card
                    className="text-center h-100 shadow-sm"
                    style={{ cursor: "pointer", transition: "box-shadow 0.15s" }}
                    onClick={() => openDetail(key)}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = ""}
                  >
                    <Card.Body className="py-3 px-2">
                      <div style={{ fontSize: "1.75rem", fontWeight: 700 }}>{count}</div>
                      <div className="text-muted" style={{ fontSize: "0.78rem" }}>{label}</div>
                      {hasTotal && (
                        <div className="text-success fw-semibold" style={{ fontSize: "0.82rem" }}>
                          {fmt$(entry?.total)}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* ── My assignments + Open by assignee ───────────────────────── */}
          <Row className="g-3 mb-4">
            {/* My pending */}
            <Col xs={12} lg={7}>
              <Card className="shadow-sm h-100">
                <Card.Header className="fw-semibold d-flex align-items-center gap-2">
                  My Pending Assignments
                  {data.my_services.length > 0 && (
                    <Badge bg="warning" text="dark">{data.my_services.length}</Badge>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  {data.my_services.length === 0 ? (
                    <p className="text-muted p-3 mb-0">No pending tasks assigned to you.</p>
                  ) : (
                    <Table hover responsive className="mb-0" size="sm">
                      <thead className="table-light">
                        <tr><th>Client</th><th>Service</th><th>Requested By</th><th>Date</th><th>Status</th><th></th></tr>
                      </thead>
                      <tbody>
                        {data.my_services.map((s) => (
                          <tr key={s.service_id}>
                            <td>{s.client_name}</td>
                            <td>{s.service_type}</td>
                            <td>{s.created_by_name || <span className="text-muted">—</span>}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{s.service_date}</td>
                            <td><StatusBadge status={s.status} /></td>
                            <td>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => viewOnClientPage(s.client_id, s.service_id)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Open by assignee */}
            <Col xs={12} lg={5}>
              <Card className="shadow-sm h-100">
                <Card.Header className="fw-semibold">Open Requests by Assignee</Card.Header>
                <Card.Body className="p-0">
                  {data.open_by_assignee.length === 0 ? (
                    <p className="text-muted p-3 mb-0">No open service requests.</p>
                  ) : (
                    <Table hover responsive className="mb-0" size="sm">
                      <thead className="table-light">
                        <tr><th>Assignee</th><th className="text-end">Open</th></tr>
                      </thead>
                      <tbody>
                        {data.open_by_assignee.map((row, i) => (
                          <tr
                            key={i}
                            style={{ cursor: row.user_id != null ? "pointer" : "default" }}
                            onClick={() => row.user_id != null && openAssigneeModal(row.user_id, row.name)}
                          >
                            <td>
                              <span className={row.user_id != null ? "text-primary" : "text-muted"}>
                                {row.name}
                              </span>
                            </td>
                            <td className="text-end">
                              <Badge bg={row.name === "Unassigned" ? "danger" : "warning"} text="dark">
                                {row.count}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── My open tasks ─────────────────────────────────────────────── */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold d-flex align-items-center gap-2">
                  My Open Tasks
                  {(data.my_tasks?.length || 0) > 0 && (
                    <Badge bg="warning" text="dark">{data.my_tasks.length}</Badge>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  {(data.my_tasks?.length || 0) === 0 ? (
                    <p className="text-muted p-3 mb-0">No open tasks assigned to you.</p>
                  ) : (
                    <Table hover responsive className="mb-0" size="sm">
                      <thead className="table-light">
                        <tr><th>Client</th><th>Task</th><th>Created</th><th>Status</th><th></th></tr>
                      </thead>
                      <tbody>
                        {data.my_tasks.map((t) => (
                          <tr key={t.task_id}>
                            <td><Link to={`/clients/${t.client_id}/view`}>{t.client_name}</Link></td>
                            <td>
                              <span className="fst-italic">{t.task_text}</span>
                              {t.status === "Returned" && t.return_note && (
                                <div className="small text-danger mt-1">
                                  <i className="bi bi-arrow-return-left me-1" />
                                  Sent back{t.returned_by_name ? ` by ${t.returned_by_name}` : ""}: {t.return_note}
                                </div>
                              )}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>{t.service_date}</td>
                            <td><StatusBadge status={t.status} /></td>
                            <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                              <Button
                                size="sm"
                                variant="outline-success"
                                disabled={completingTaskId === t.task_id}
                                onClick={() => completeTask(t.task_id)}
                              >
                                {completingTaskId === t.task_id
                                  ? <Spinner animation="border" size="sm" />
                                  : <><i className="bi bi-check2-circle me-1" />Mark Complete</>}
                              </Button>
                              {t.status === "Open" && (
                                <Button
                                  size="sm"
                                  variant="outline-warning"
                                  className="ms-2"
                                  onClick={() => setReturningTask(t)}
                                >
                                  <i className="bi bi-arrow-return-left me-1" />
                                  Unable to Complete
                                </Button>
                              )}
                              <Dropdown className="d-inline-block ms-2">
                                <Dropdown.Toggle
                                  size="sm"
                                  variant="outline-primary"
                                  disabled={assigningTaskId === t.task_id}
                                >
                                  {assigningTaskId === t.task_id
                                    ? <Spinner animation="border" size="sm" />
                                    : <><i className="bi bi-person-up me-1" />Reassign</>}
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end" style={{ maxHeight: 300, overflowY: "auto" }}>
                                  {assignees.length === 0 ? (
                                    <Dropdown.Header>No one available</Dropdown.Header>
                                  ) : (
                                    assignees.map((w) => (
                                      <Dropdown.Item
                                        key={w.id}
                                        disabled={w.id === t.assigned_to}
                                        onClick={() => assignTask(t.task_id, w.id)}
                                      >
                                        {w.full_name}
                                        {w.id === t.assigned_to && (
                                          <span className="text-muted small ms-1">(current)</span>
                                        )}
                                      </Dropdown.Item>
                                    ))
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Recently completed tasks (tasks I created) ────────────────── */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold d-flex align-items-center gap-2">
                  Recently Completed Tasks
                  {(data.completed_tasks?.length || 0) > 0 && (
                    <Badge bg="success">{data.completed_tasks.length}</Badge>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  {(data.completed_tasks?.length || 0) === 0 ? (
                    <p className="text-muted p-3 mb-0">No tasks you created have been completed recently.</p>
                  ) : (
                    <Table hover responsive className="mb-0" size="sm">
                      <thead className="table-light">
                        <tr><th>Client</th><th>Task</th><th>Completed By</th><th>Completed</th></tr>
                      </thead>
                      <tbody>
                        {data.completed_tasks.map((t) => (
                          <tr key={t.task_id}>
                            <td><Link to={`/clients/${t.client_id}/view`}>{t.client_name}</Link></td>
                            <td className="fst-italic">{t.task_text}</td>
                            <td>{t.assignee_name || "—"}</td>
                            <td>
                              {t.completed_at
                                ? new Date(t.completed_at).toLocaleString("en-US", {
                                    timeZone: "America/Detroit",
                                    month: "2-digit", day: "2-digit", year: "numeric",
                                    hour: "numeric", minute: "2-digit", hour12: true,
                                  })
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Team activity ────────────────────────────────────────────── */}
          <Card className="shadow-sm">
            <Card.Header className="fw-semibold">
              Team Activity — {periodLabel}
            </Card.Header>
            <Card.Body className="p-0">
              {data.team_activity.length === 0 ? (
                <p className="text-muted p-3 mb-0">No team members found.</p>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Worker</th>
                      <th>Role(s)</th>
                      <th className="text-end">Services</th>
                      <th className="text-end">Clients Helped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.team_activity.map((w) => (
                      <tr key={w.user_id}>
                        <td className="fw-semibold">{w.full_name}</td>
                        <td>
                          {w.roles.map((r) => (
                            <Badge key={r} bg="light" text="dark" className="me-1 border">
                              {r}
                            </Badge>
                          ))}
                        </td>
                        <td className="text-end">{w.services}</td>
                        <td className="text-end">{w.clients_helped}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* ── Services report parameter modal ──────────────────────────────── */}
      <Modal show={reportModal} onHide={() => setReportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Services Summary Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">Choose a reporting period, then generate the report.</p>
          <Form>
            {REPORT_PERIOD_OPTIONS.map((p) => (
              <Form.Check
                key={p.key}
                type="radio"
                name="reportPeriod"
                id={`report-period-${p.key}`}
                label={p.label}
                value={p.key}
                checked={reportPeriod === p.key}
                onChange={(e) => setReportPeriod(e.target.value)}
                className="mb-2"
              />
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReportModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={generateReport}>Generate Report</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Assignee services modal ──────────────────────────────────────── */}
      <Modal show={assigneeModal.show} onHide={closeAssigneeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{assigneeModal.name} — Open Services</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {assigneeModal.error && (
            <Alert variant="danger" className="m-3">{assigneeModal.error}</Alert>
          )}
          {assigneeModal.loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : assigneeModal.items.length === 0 ? (
            <p className="text-muted p-3 mb-0">No open services found.</p>
          ) : (
            <Table hover responsive className="mb-0" size="sm">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Client</th>
                  <th>Service Type</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {assigneeModal.items.map((s) => (
                  <tr key={s.service_id}>
                    <td className="fw-semibold">{s.client_name}</td>
                    <td>{s.service_type}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{s.service_date}</td>
                    <td><StatusBadge status={s.status} /></td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => viewOnClientPage(s.client_id, s.service_id)}
                      >
                        View on Client Page
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="text-muted small me-auto">
            {assigneeModal.items.length} open service{assigneeModal.items.length !== 1 ? "s" : ""}
          </span>
          <Button variant="secondary" onClick={closeAssigneeModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Service detail modal ──────────────────────────────────────────── */}
      <Modal
        show={!!detailType}
        onHide={closeDetail}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {SERVICE_CARDS.find((c) => c.key === detailType)?.label} — {periodLabel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {detailError && <Alert variant="danger" className="m-3">{detailError}</Alert>}
          {detailLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : detailData && (
            <>
              {detailData.items.length === 0 ? (
                <p className="text-muted p-3 mb-0">No records found for this period.</p>
              ) : (
                <Table hover responsive className="mb-0" size="sm">
                  <thead className="table-light sticky-top">
                    <tr>
                      {(DETAIL_COLS[detailType] || []).map((col) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.items.map((row, i) => (
                      <tr key={i}>
                        {(DETAIL_COLS[detailType] || []).map((col) => (
                          <td key={col.key}>
                            {col.render
                              ? col.render(row[col.key], row)
                              : row[col.key] ?? "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="text-muted small me-auto">
            {detailData?.items?.length ?? 0} record{detailData?.items?.length !== 1 ? "s" : ""}
          </span>
          <Button variant="secondary" onClick={closeDetail}>Close</Button>
        </Modal.Footer>
      </Modal>

      {returningTask && (
        <TaskReturnModal
          task={returningTask}
          onCancel={() => setReturningTask(null)}
          onSubmit={returnTask}
        />
      )}
    </>
  );
}
